import { useEffect, useRef, useState } from 'react';
import { ChevronDown, FileDown, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import type { IpSeed } from '@starfox/shared-types';

export interface Dialogue {
  speaker: string;
  text: string;
  emotion?: string;
  type?: 'speech' | 'inner_monologue' | 'action';
}

export interface CameraSpec {
  shot?: string;
  move?: string;
  durationSec?: number;
}

export interface Scene {
  sceneId: string;
  location?: string;
  action?: string;
  props?: string[];
  dialogues?: Dialogue[];
  cameras?: CameraSpec[];
  durationSec?: number;
}

export interface EpisodeRecord {
  index: number;
  title?: string;
  summary?: string;
  scenes?: Scene[];
  charCount?: number;
  updatedAt?: string;
  densityIssues?: string[];
  densityCheckedAt?: string;
  rhythmIssues?: string[];
  rhythmCheckedAt?: string;
  rhythmRepair?: {
    originalSceneCount?: number;
    sceneCount?: number;
    splitSceneCount?: number;
    splitDialogueLineCount?: number;
    durationSec?: number;
    charCount?: number;
  };
  shotReadinessIssues?: string[];
  shotReadinessCheckedAt?: string;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildScriptPlain(episodes: EpisodeRecord[], ipSeed: IpSeed | null): string {
  const out: string[] = [];
  if (ipSeed) {
    out.push('━'.repeat(40));
    out.push(`IP 名称:${ipSeed.ipName || '—'}`);
    out.push(`剧情集数:共 ${episodes.length} 集`);
    if (ipSeed.storyline) {
      out.push('');
      out.push('【剧情大纲】');
      out.push(ipSeed.storyline);
    }
    const mains: string[] = [];
    if (ipSeed.mcArchetype) mains.push(`主角 · ${ipSeed.mcArchetype}`);
    if (ipSeed.antagonistArchetype) mains.push(`反派 · ${ipSeed.antagonistArchetype}`);
    if (mains.length > 0) {
      out.push('');
      out.push('【主要人物】');
      mains.forEach((m) => out.push(`- ${m}`));
    }
    if (ipSeed.cheerPoints && ipSeed.cheerPoints.length > 0) {
      out.push('');
      out.push('【爽点节奏】');
      ipSeed.cheerPoints.forEach((c, i) => out.push(`${i + 1}. ${c}`));
    }
    out.push('━'.repeat(40));
    out.push('');
    out.push('');
  }
  episodes.forEach((ep) => {
    const head = `第 ${ep.index} 集${ep.title ? ` · ${ep.title}` : ''}`;
    out.push(head);
    out.push('='.repeat(40));
    if (ep.summary) {
      out.push(`【概览】${ep.summary}`);
      out.push('');
    }
    (ep.scenes ?? []).forEach((sc) => {
      const loc = sc.location ? `  ${sc.location}` : '';
      out.push(`◆ ${sc.sceneId}${loc}`);
      if (sc.props && sc.props.length > 0) out.push(`  【关键道具】${sc.props.join('、')}`);
      if (sc.action) out.push(`  (动作) ${sc.action}`);
      (sc.dialogues ?? []).forEach((d) => {
        const tag = d.emotion ? `[${d.emotion}] ` : '';
        if (d.type === 'action') {
          out.push(`  · ${d.speaker} ${d.text}`);
        } else if (d.type === 'inner_monologue') {
          out.push(`  ${d.speaker} · OS:${tag}${d.text}`);
        } else {
          out.push(`  ${d.speaker}:${tag}${d.text}`);
        }
      });
      out.push('');
    });
    out.push('');
    out.push('');
  });
  return out.join('\n');
}

export function buildScriptHtml(episodes: EpisodeRecord[], ipSeed: IpSeed | null): string {
  const body: string[] = [];
  if (ipSeed) {
    body.push(
      `<div style="border:1pt solid #c7d2fe;background:#eef2ff;padding:14pt 18pt;margin-bottom:18pt;">`,
    );
    body.push(
      `<h1 style="margin:0 0 8pt 0;color:#3730a3;font-family:'Microsoft YaHei',sans-serif;">${escapeHtml(ipSeed.ipName || '未命名 IP')}</h1>`,
    );
    body.push(
      `<p style="margin:0 0 8pt 0;color:#4338ca;font-size:11pt;">剧情集数:共 <b>${episodes.length}</b> 集</p>`,
    );
    if (ipSeed.storyline) {
      body.push(`<h3 style="margin:10pt 0 4pt 0;color:#4338ca;">【剧情大纲】</h3>`);
      body.push(
        `<p style="margin:0 0 8pt 0;line-height:1.7;color:#1f2937;">${escapeHtml(ipSeed.storyline)}</p>`,
      );
    }
    const mains: string[] = [];
    if (ipSeed.mcArchetype) mains.push(`主角 · ${ipSeed.mcArchetype}`);
    if (ipSeed.antagonistArchetype) mains.push(`反派 · ${ipSeed.antagonistArchetype}`);
    if (mains.length > 0) {
      body.push(`<h3 style="margin:10pt 0 4pt 0;color:#4338ca;">【主要人物】</h3>`);
      body.push('<ul style="margin:0 0 8pt 0;padding-left:20pt;">');
      mains.forEach((m) => body.push(`<li style="margin:2pt 0;">${escapeHtml(m)}</li>`));
      body.push('</ul>');
    }
    if (ipSeed.cheerPoints && ipSeed.cheerPoints.length > 0) {
      body.push(`<h3 style="margin:10pt 0 4pt 0;color:#4338ca;">【爽点节奏】</h3>`);
      body.push('<ol style="margin:0;padding-left:20pt;">');
      ipSeed.cheerPoints.forEach((c) =>
        body.push(`<li style="margin:2pt 0;line-height:1.6;">${escapeHtml(c)}</li>`),
      );
      body.push('</ol>');
    }
    body.push('</div>');
    body.push('<p style="page-break-after:always;">&nbsp;</p>');
  }
  episodes.forEach((ep) => {
    body.push(
      `<h1 style="font-family:'Microsoft YaHei',sans-serif;">第 ${ep.index} 集${ep.title ? ` · ${escapeHtml(ep.title)}` : ''}</h1>`,
    );
    if (ep.summary) {
      body.push(`<p><b>【概览】</b>${escapeHtml(ep.summary)}</p>`);
    }
    (ep.scenes ?? []).forEach((sc) => {
      body.push(
        `<h3 style="margin-top:14pt;color:#4338ca;">◆ ${escapeHtml(sc.sceneId)}${sc.location ? `  ${escapeHtml(sc.location)}` : ''}</h3>`,
      );
      if (sc.props && sc.props.length > 0) {
        body.push(
          `<p style="margin:4pt 0;color:#7c3aed;font-size:10pt;"><b>【关键道具】</b>${escapeHtml(sc.props.join('、'))}</p>`,
        );
      }
      if (sc.action) {
        const actionHtml = escapeHtml(sc.action).replace(/\n/g, '<br/>');
        body.push(
          `<p style="color:#6b7280;font-style:italic;">(动作) ${actionHtml}</p>`,
        );
      }
      (sc.dialogues ?? []).forEach((d) => {
        const tag = d.emotion
          ? `<span style="color:#9ca3af;font-size:10pt;">[${escapeHtml(d.emotion)}]</span> `
          : '';
        if (d.type === 'action') {
          body.push(
            `<p style="color:#6b7280;font-style:italic;margin:4pt 0;">· ${escapeHtml(d.speaker)} ${escapeHtml(d.text)}</p>`,
          );
        } else if (d.type === 'inner_monologue') {
          body.push(
            `<p style="background:#f5f3ff;padding:4pt 8pt;border-left:3pt solid #a78bfa;margin:4pt 0;font-style:italic;color:#5b21b6;"><b>${escapeHtml(d.speaker)} · OS:</b> ${tag}${escapeHtml(d.text)}</p>`,
          );
        } else {
          body.push(
            `<p style="margin:4pt 0;"><b style="color:#4338ca;">${escapeHtml(d.speaker)}:</b> ${tag}${escapeHtml(d.text)}</p>`,
          );
        }
      });
    });
    body.push('<p style="page-break-after:always;">&nbsp;</p>');
  });
  return (
    "<html xmlns:o='urn:schemas-microsoft-com:office:office' " +
    "xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>" +
    "<head><meta charset='utf-8'><title>剧本</title></head>" +
    `<body style="font-family:'Microsoft YaHei',sans-serif;font-size:11pt;line-height:1.6;">${body.join('\n')}</body></html>`
  );
}

function triggerDownload(filename: string, mime: string, content: string) {
  const bom = mime.startsWith('text/') ? '\ufeff' : '';
  const blob = new Blob([bom + content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportScript(
  format: 'txt' | 'doc',
  episodes: EpisodeRecord[],
  ipSeed: IpSeed | null,
) {
  if (episodes.length === 0) {
    toast.warning('暂无可导出的剧本', '请先生成分集脚本');
    return;
  }
  const ts = new Date().toISOString().slice(0, 10);
  const ipName = ipSeed?.ipName?.trim() || '剧本';
  if (format === 'txt') {
    const text = buildScriptPlain(episodes, ipSeed);
    triggerDownload(`${ipName}-${episodes.length}集-${ts}.txt`, 'text/plain;charset=utf-8', text);
    toast.success('已导出 TXT', `${episodes.length} 集 · ${text.length} 字`);
  } else {
    const html = buildScriptHtml(episodes, ipSeed);
    triggerDownload(`${ipName}-${episodes.length}集-${ts}.doc`, 'application/msword', html);
    toast.success('已导出 Word', `${episodes.length} 集 · 用 Word / WPS 打开`);
  }
}

interface ExportScriptDropdownProps {
  episodes: EpisodeRecord[];
  ipSeed: IpSeed | null;
}

export function ExportScriptDropdown({ episodes, ipSeed }: ExportScriptDropdownProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const disabled = episodes.length === 0;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const pick = (fmt: 'txt' | 'doc') => {
    setOpen(false);
    exportScript(fmt, episodes, ipSeed);
  };

  return (
    <div ref={wrapRef} className="relative">
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title={disabled ? '尚无分集脚本可导出' : `导出全部 ${episodes.length} 集剧本`}
      >
        <FileDown className="h-3.5 w-3.5" />
        导出{episodes.length > 0 ? `(${episodes.length} 集)` : ''}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </Button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-40 overflow-hidden rounded-lg border border-outline-variant/40 bg-surface shadow-lg">
          <button
            type="button"
            onClick={() => pick('txt')}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-neutral-800 transition hover:bg-brand-50 hover:text-brand-700"
          >
            <FileText className="h-3.5 w-3.5" />
            TXT 纯文本
          </button>
          <button
            type="button"
            onClick={() => pick('doc')}
            className="flex w-full items-center gap-2 border-t border-outline-variant/30 px-3 py-2 text-left text-sm text-neutral-800 transition hover:bg-brand-50 hover:text-brand-700"
          >
            <FileDown className="h-3.5 w-3.5" />
            Word 文档
          </button>
        </div>
      )}
    </div>
  );
}
